import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import type { Branch } from "@/types/domain";

// Same fixed, reused palette as DepartmentDonut - a branch keeps its colour
// across a session rather than reshuffling per render.
const PALETTE = ["#6c3fd4", "#ea4c89", "#8b5cf6", "#bb1656", "#0d9488", "#b45309", "#4f46e5", "#6e6d7a"];

export function BranchDonut({ branches }: { branches: Branch[] }) {
  const rows = branches
    .filter((b) => (b.employee_count ?? 0) > 0)
    .sort((a, b) => (b.employee_count ?? 0) - (a.employee_count ?? 0));
  const total = rows.reduce((sum, b) => sum + (b.employee_count ?? 0), 0);

  if (rows.length === 0) {
    return <p className="flex h-56 items-center justify-center text-sm text-slate-400">No employees assigned to a branch yet.</p>;
  }

  return (
    <div className="flex flex-col items-center gap-5 sm:flex-row">
      <div className="h-48 w-48 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          {/* accessibilityLayer=false + Pie's own rootTabIndex=-1 - see
              DepartmentDonut.tsx's full explanation. Both default tabIndex
              paths (chart root <svg> and Pie's own wrapping <g>) draw an
              unstyled black browser focus rectangle when clicked; the
              legend list beside this chart already carries the same data as
              accessible HTML, so disabling both is safe. */}
          <PieChart accessibilityLayer={false}>
            <Pie
              data={rows}
              dataKey="employee_count"
              nameKey="name"
              innerRadius={55}
              outerRadius={80}
              paddingAngle={2}
              // A real but small branch (e.g. 1 person at a satellite
              // office) would otherwise render as a near-invisible sliver,
              // easy to mistake for a rendering glitch rather than
              // genuine data - a minimum angle keeps every branch visibly
              // a wedge.
              minAngle={3}
              strokeWidth={0}
              rootTabIndex={-1}
            >
              {rows.map((b, i) => (
                <Cell key={b.id} fill={PALETTE[i % PALETTE.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="w-full min-w-0 flex-1 space-y-2">
        {rows.map((b, i) => (
          <li key={b.id} className="flex items-center justify-between gap-2 text-sm">
            <span className="flex min-w-0 items-center gap-2 text-slate-600">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
              <span className="truncate">{b.name}</span>
            </span>
            <span className="shrink-0 font-medium text-slate-800">
              {b.employee_count} <span className="text-slate-400">({(((b.employee_count ?? 0) / total) * 100).toFixed(1)}%)</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
